import { useEffect, useRef } from 'react';
import { report } from '@shared/reporting';
import type { BaseEvent } from '@shared/reporting';
import type { PatientProfileCreateResponse } from '../types/patientprofile/PatientProfileCreateResponse';

interface UseWaitingRoomAnalyticsOptions {
  /** Patient profile data */
  profile: PatientProfileCreateResponse['profile'] | null | undefined;
  /** Whether profile is still loading */
  loading: boolean;
  /** User session data */
  session: { user: { id: string } } | null | undefined;
  /** Base URL of the doxy.me instance */
  doxyBaseUrl: string | null;
}

/**
 * @group Hooks
 * 
 * @summary
 * <blockquote>Hook that emits analytics events when waiting room is accessed.</blockquote>
 * 
 * @remarks
 * - Emits event only once per component mount
 * - Handles errors gracefully without blocking user flow
 * - Only emits when profile and doxyBaseUrl are available
 * 
 * @param options - Configuration options for analytics
 * 
 * @example
 * ```tsx
 * const { profile, loading } = usePatientProfile();
 * const { session } = useClientSession();
 * useWaitingRoomAnalytics({
 *   profile,
 *   loading,
 *   session,
 *   doxyBaseUrl: 'https://aiyeza.doxy.me'
 * });
 * ```
 */
export function useWaitingRoomAnalytics({
  profile,
  loading,
  session,
  doxyBaseUrl
}: UseWaitingRoomAnalyticsOptions): void {
  const analyticsEmitted = useRef(false);

  useEffect(() => {
    // Only emit analytics if profile exists, not loading, and URL is ready
    if (profile && !loading && doxyBaseUrl && !analyticsEmitted.current) {
      // Fire-and-forget analytics event
      const event: BaseEvent = {
        distinctId: session?.user?.id || 'anonymous',
        name: 'waiting_room_accessed',
        props: {
          patient_id: profile.userId,
          doxy_url_base: doxyBaseUrl
        }
      };
      report(event);
      analyticsEmitted.current = true;
    }
  }, [profile, loading, session, doxyBaseUrl]);
}

